/**
 * Cloudflare Images URL Utilities
 *
 * Cloudflare Images supports "variants" - different transformations of the same image
 * accessible via URL suffix. This is FREE and built-in to Cloudflare Images.
 *
 * URL Format: https://imagedelivery.net/{account_hash}/{image_id}/{variant}
 *
 * Variants must be created in Cloudflare Dashboard → Images → Variants:
 * - "public" (default) - Original image
 * - "marketing" - Cropped version with bottom removed (for watermark removal)
 *
 * @see https://developers.cloudflare.com/images/transform-images/transform-via-url/
 */

export type CloudflareImageVariant = 'public' | 'marketing' | 'thumbnail';

/**
 * Standard Cloudflare Images URL pattern
 * Example: https://imagedelivery.net/abc123/image-id-here/public
 */
const CLOUDFLARE_IMAGES_PATTERN = /^https:\/\/imagedelivery\.net\/[^/]+\/[^/]+\/[^/]+$/;

/**
 * Check if a URL is a Cloudflare Images URL
 */
export function isCloudflareImageUrl(url: string): boolean {
  return CLOUDFLARE_IMAGES_PATTERN.test(url);
}

/**
 * Get an image URL with a specific variant
 *
 * @param imageUrl - Original Cloudflare Images URL (e.g., .../public)
 * @param variant - Target variant name (e.g., 'marketing')
 * @returns URL with the variant suffix, or original URL if not a Cloudflare URL
 *
 * @example
 * getImageVariant('https://imagedelivery.net/abc/img123/public', 'marketing')
 * // Returns: 'https://imagedelivery.net/abc/img123/marketing'
 */
export function getImageVariant(
  imageUrl: string,
  variant: CloudflareImageVariant = 'public'
): string {
  if (!imageUrl) return imageUrl;

  // Handle Cloudflare Images URLs
  if (isCloudflareImageUrl(imageUrl)) {
    return imageUrl.replace(/\/[^/]+$/, `/${variant}`);
  }

  // For non-Cloudflare URLs, return as-is
  // This allows the function to be used safely on any image URL
  return imageUrl;
}

/**
 * Get the cropped/marketing version of an image (watermark removed)
 * Convenience wrapper for getImageVariant
 */
export function getCroppedImageUrl(imageUrl: string): string {
  return getImageVariant(imageUrl, 'marketing');
}

/**
 * Get the original/public version of an image
 * Useful for "reset" functionality
 */
export function getOriginalImageUrl(imageUrl: string): string {
  return getImageVariant(imageUrl, 'public');
}

/**
 * Extract the current variant from a Cloudflare Images URL
 */
export function getCurrentVariant(imageUrl: string): CloudflareImageVariant | null {
  if (!imageUrl || !isCloudflareImageUrl(imageUrl)) return null;

  const match = imageUrl.match(/\/([^/]+)$/);
  if (match) {
    return match[1] as CloudflareImageVariant;
  }
  return null;
}

/**
 * Check if an image is currently using the cropped/marketing variant
 */
export function isCroppedVariant(imageUrl: string): boolean {
  return getCurrentVariant(imageUrl) === 'marketing';
}

/**
 * Toggle between original and cropped variants
 */
export function toggleImageVariant(imageUrl: string): string {
  const current = getCurrentVariant(imageUrl);
  if (current === 'marketing') {
    return getOriginalImageUrl(imageUrl);
  }
  return getCroppedImageUrl(imageUrl);
}
